/**
 * AUTOMATION MAPPERS — conversione tra modello UI e DTO REST.
 *
 * A differenza di taskMappers, l'unico campo con conversione enum e' `status`
 * (int32 nel DTO, union string in UI). Gli enum delle condizioni
 * (`fieldType`/`valueSource`) viaggiano come NOME dell'enum (string) su entrambi
 * i lati: passano 1:1, la union TS li vincola senza mapper. Resta int32 grezzo
 * solo `onError` (azioni), finché il blocco ALLORA non è cablato. Quindi
 * l'albero passa 1:1 e il mapping si riduce a spread + override di `status`.
 *
 * L'eccezione e' il VALORE di una condizione (`AutomationCondition.value`), che
 * non passa 1:1: l'editor del blocco SE lo tiene come stringa grezza, il backend
 * lo vuole come oggetto JSON. La coppia `conditionValueToDto`/`FromDto` in fondo
 * al file e' l'unico punto di traduzione.
 *
 * SYNC CROSS-LAYER — se il backend cambia i valori di stato, aggiornare
 * `AUTOMATION_STATUS_TO_INT` qui insieme a:
 *   - `automationTypes.ts` (union `AutomationStatus`)
 *   - `AutomationList.tsx` (STATUS_CHIP_COLOR / STATUS_FILTERS)
 *   - `locales/{it,en}.json` (automationsInsights.list.statuses.*)
 *   - Java: modules/crm-automation/crm-automation-api/.../constants/AutomationDefinitionStatus.java
 */

import { enumFromDto } from '@crm/shared';
import type {
  Automation,
  AutomationCreatePayload,
  AutomationParam,
  AutomationParamType,
  AutomationExecution,
  AutomationExecutionStatus,
  AutomationRun,
  AutomationRunStatus,
  AutomationStatus,
  EditableValueSource,
  OperatorCardinality,
} from './automationTypes';
import { DEFAULT_RELATIVE_UNIT } from './automationTypes';

// SYNC with AutomationDefinitionStatus.java: DRAFT=1, ACTIVE=2, SUSPENDED=3, ARCHIVED=4
const AUTOMATION_STATUS_TO_INT: Record<AutomationStatus, number> = {
  draft: 1,
  active: 2,
  suspended: 3,
  archived: 4,
};
const AUTOMATION_STATUS_FROM_INT: Record<number, AutomationStatus> = {
  1: 'draft',
  2: 'active',
  3: 'suspended',
  4: 'archived',
};

const automationStatusToDto = (v: AutomationStatus): number => AUTOMATION_STATUS_TO_INT[v];
const automationStatusFromDto = (v: number): AutomationStatus =>
  enumFromDto('AutomationDefinitionStatus', v, AUTOMATION_STATUS_FROM_INT, 'draft');

/**
 * Shape read del backend: identica ad `Automation` salvo `status` (int32).
 * Usata solo dentro automationApi/automationMappers; lo store/UI vede solo
 * `Automation`.
 */
export interface AutomationDto extends Omit<Automation, 'status'> {
  status: number;
}

/** DTO -> modello UI: chiamato sulle response list/getOne/transizioni. */
export function automationFromDto(dto: AutomationDto): Automation {
  return { ...dto, status: automationStatusFromDto(dto.status) };
}

/** Modello UI -> DTO: usato dai mock MSW per simulare la shape backend. */
export function automationToDto(a: Automation): AutomationDto {
  return { ...a, status: automationStatusToDto(a.status) };
}

/**
 * Lunghezza massima di `AutomationDefinition.name`, come la dichiara il model hint della colonna
 * (`portlet-model-hints.xml`). Duplicata qui perché il client non ha modo di leggerla dal server:
 * se un giorno la colonna cambiasse, questa costante va aggiornata a mano — il disallineamento si
 * manifesterebbe come una creazione rifiutata dal backend, non come un errore in compilazione.
 */
const NAME_MAX_LENGTH = 255;

/**
 * Template (record) -> payload di creazione di un'AUTOMAZIONE da quel template ("Usa template"). Copia
 * testata e albero di configurazione, forza `template: false` e scarta i campi gestiti dal server (id,
 * audit, version, status, ...); `value` ed `eventConfig` sono già stringhe 1:1. La nuova automazione
 * nasce DRAFT lato server e si atterra sul suo editor (/:id/edit), dove il nome si può cambiare.
 *
 * L'albero passa dal modello all'Input perdendo gli id, con un'eccezione: le azioni portano
 * `automationActionId` a `0`, che per il server significa «azione NUOVA, da creare». Non è lo zero
 * di un campo dimenticato — copiando un template le azioni nascono adesso, e l'id del template non
 * deve seguirle, o si chiederebbe al server di aggiornare in place le azioni di quello.
 *
 * Il NOME arriva da fuori invece di essere ereditato tale e quale: una copia che si chiama come il
 * template rende le due schermate indistinguibili — la configurazione è identica per definizione,
 * quindi il titolo è l'unica cosa che può dire su quale dei due record ci si trova — e lascia
 * nell'elenco due righe con lo stesso nome. Arriva come parametro e non composto qui perché è testo
 * da tradurre, e questo modulo è puro: le etichette le ha chi ha `t`.
 *
 * Il nome viene TRONCATO a {@link NAME_MAX_LENGTH}: un prefisso aggiunto a un nome già vicino al
 * limite lo supererebbe, e il backend rifiuterebbe la creazione con un errore di validazione — cioè
 * un toast rosso su un click che all'utente sembrava innocuo, per un nome che non ha nemmeno scelto
 * lui. Meglio un nome accorciato, che si vede e si corregge, di un'operazione che non parte.
 *
 * @param template il template da cui copiare
 * @param name     nome della nuova automazione; assente eredita quello del template
 */
export function createPayloadFromTemplate(
  template: Automation,
  name?: string,
): AutomationCreatePayload {
  const copyName = name ?? template.name;

  return {
    // I puntini di sospensione occupano un carattere, quindi si taglia a MAX-1: sono il segno che
    // dice all'utente che il nome è stato accorciato invece di essere stato scritto così.
    name:
      copyName.length > NAME_MAX_LENGTH
        ? `${copyName.slice(0, NAME_MAX_LENGTH - 1)}…`
        : copyName,
    description: template.description,
    className: template.className,
    eventType: template.eventType,
    automationCategoryId: template.automationCategoryId,
    priority: template.priority,
    template: false,
    eventConfig: template.eventConfig,
    repeatable: template.repeatable,
    logEveryRecord: template.logEveryRecord,
    conditionGroups: template.conditionGroups.map((group) => ({
      priority: group.priority,
      conditions: group.conditions.map((condition) => ({
        fieldName: condition.fieldName,
        fieldType: condition.fieldType,
        operator: condition.operator,
        value: condition.value,
        valueSource: condition.valueSource,
        priority: condition.priority,
      })),
    })),
    actions: template.actions.map((action) => ({
      automationActionId: 0,
      code: action.code,
      title: action.title,
      config: action.config,
      onError: action.onError,
      retryCount: action.retryCount,
      retryDelaySeconds: action.retryDelaySeconds,
      priority: action.priority,
      enabled: action.enabled,
    })),
  };
}

// ─── AutomationRun (log di monitoraggio) ───────────────────────────────

// SYNC with AutomationRunStatus.java: RUNNING=1, SUCCESS=2, FAILED=3
const AUTOMATION_RUN_STATUS_TO_INT: Record<AutomationRunStatus, number> = {
  running: 1,
  success: 2,
  failed: 3,
};
const AUTOMATION_RUN_STATUS_FROM_INT: Record<number, AutomationRunStatus> = {
  1: 'running',
  2: 'success',
  3: 'failed',
};

const automationRunStatusToDto = (v: AutomationRunStatus): number =>
  AUTOMATION_RUN_STATUS_TO_INT[v];
const automationRunStatusFromDto = (v: number): AutomationRunStatus =>
  enumFromDto('AutomationRunStatus', v, AUTOMATION_RUN_STATUS_FROM_INT, 'running');

/** Shape read del backend per un run: identica ad `AutomationRun` salvo `status` (int32). */
export interface AutomationRunDto extends Omit<AutomationRun, 'status'> {
  status: number;
}

/** DTO -> modello UI di un run (status int -> string). */
export function automationRunFromDto(dto: AutomationRunDto): AutomationRun {
  return { ...dto, status: automationRunStatusFromDto(dto.status) };
}

/** Modello UI -> DTO di un run: usato dai mock MSW. */
export function automationRunToDto(run: AutomationRun): AutomationRunDto {
  return { ...run, status: automationRunStatusToDto(run.status) };
}

// ─── AutomationExecution (esiti per record, drill-down del run) ─────────

// SYNC with AutomationExecutionStatus.java: RUNNING=1, SUCCESS=2, FAILED=3, SKIPPED=4,
// ALREADY_EXECUTED=5, LOOP_PREVENTED=6
const AUTOMATION_EXECUTION_STATUS_FROM_INT: Record<number, AutomationExecutionStatus> = {
  1: 'running',
  2: 'success',
  3: 'failed',
  4: 'skipped',
  5: 'already-executed',
  6: 'loop-prevented',
};

// Nessuna mappa string -> int, al contrario del run: gli esiti per record si leggono soltanto.
// Il gemello `automationRunToDto` esiste per i mock MSW, che il log dei run lo simulano; qui
// aggiungerla ora vorrebbe dire scrivere codice che nessuno chiama.
const automationExecutionStatusFromDto = (v: number): AutomationExecutionStatus =>
  enumFromDto('AutomationExecutionStatus', v, AUTOMATION_EXECUTION_STATUS_FROM_INT, 'running');

/** Shape read del backend per un esito: identica ad `AutomationExecution` salvo `status` (int32). */
export interface AutomationExecutionDto extends Omit<AutomationExecution, 'status'> {
  status: number;
}

/** DTO -> modello UI di un esito (status int -> string). */
export function automationExecutionFromDto(dto: AutomationExecutionDto): AutomationExecution {
  return { ...dto, status: automationExecutionStatusFromDto(dto.status) };
}

// ─── Configurazione dell'evento (blocco QUANDO): editor -> AutomationDefinition.eventConfig ──
//
// `eventConfig` è una MAPPA `nomeParametro -> valore`, una voce per ogni Param dichiarato
// dall'evento nel catalogo. Il valore non viaggia mai nudo: è un oggetto con la stessa grammatica
// del valore di una condizione (`{"value": …}` / `{"from": …, "to": …}`, più `currency` sui MONEY),
// con i contenuti TIPIZZATI dal ParamType — numeri come numeri, booleani come booleani.
//
//   {"days": {"value": 3}, "amount": {"from": 100, "to": 500, "currency": "EUR"}}
//
// L'editor (ParamField) tiene invece stringhe grezze, come per le condizioni: la tipizzazione
// avviene QUI, al boundary. Coercizzare a ogni battuta renderebbe impossibile digitare un decimale
// ("1." diventerebbe 1, e il punto sparirebbe sotto le dita).

/**
 * Stato dell'editor -> `AutomationDefinition.eventConfig`. Un parametro non compilato NON compare:
 * la chiave viene omessa, non valorizzata a vuoto — è così che il backend distingue "non
 * configurato" (violazione solo se obbligatorio) da "valore sbagliato".
 *
 * @param params      parametri dichiarati dall'evento scelto (dal catalogo QUANDO)
 * @param eventConfig stato locale del form: scalare per i param semplici, `{from, to}` per i range
 */
export function eventConfigToDto(
  params: AutomationParam[],
  eventConfig: Record<string, unknown>,
): string {
  const config: Record<string, unknown> = {};

  for (const param of params) {
    const raw = eventConfig[param.name];

    if (param.range) {
      const { from = '', to = '' } = (raw ?? {}) as { from?: string; to?: string };
      const money = param.type === 'MONEY' ? { currency: MONEY_CURRENCY } : {};
      const range: Record<string, unknown> = {};

      // Un estremo aperto è legittimo ("da 100 in su"): si omette, non si azzera.
      if (from !== '') range.from = typedValue(param.type, from);
      if (to !== '') range.to = typedValue(param.type, to);

      if (Object.keys(range).length > 0) config[param.name] = { ...range, ...money };

      continue;
    }

    // BOOLEAN: lo Switch tiene un booleano vero, gli altri input una stringa.
    if (raw === undefined || raw === null || raw === '') continue;

    const value = typedValue(param.type, String(raw));

    // Numero incompleto ("12."): non si spedisce NaN, si tace — il campo risultera' non configurato.
    if (value === null) continue;

    config[param.name] =
      param.type === 'MONEY' ? { value, currency: MONEY_CURRENCY } : { value };
  }

  return JSON.stringify(config);
}

/**
 * `AutomationDefinition.eventConfig` (stringa JSON) -> stato dell'editor del QUANDO. Inverso di
 * {@link eventConfigToDto}. NON serve il catalogo dei param: la FORMA del valore configurato basta a
 * distinguere scalare (`{value}`) da range (`{from, to}`). I BOOLEAN restano boolean (lo Switch vuole
 * un boolean vero, non la stringa "false" che sarebbe truthy); numeri/date/stringhe tornano stringa
 * grezza, la forma che l'editor tiene mentre si digita. `currency` (sui MONEY) si ignora: la ri-mette
 * l'encode. `{}` se assente o illeggibile — una config salvata male non deve rompere l'idratazione.
 */
export function eventConfigFromDto(eventConfig: string | null): Record<string, unknown> {
  if (!eventConfig) return {};

  let parsed: Record<string, unknown>;

  try {
    const obj: unknown = JSON.parse(eventConfig);

    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return {};

    parsed = obj as Record<string, unknown>;
  } catch {
    return {};
  }

  const config: Record<string, unknown> = {};

  for (const [name, raw] of Object.entries(parsed)) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue;

    const configuredValue = raw as Record<string, unknown>;

    if ('from' in configuredValue || 'to' in configuredValue) {
      config[name] = { from: rawValue(configuredValue.from), to: rawValue(configuredValue.to) };
    } else if ('value' in configuredValue) {
      config[name] =
        typeof configuredValue.value === 'boolean'
          ? configuredValue.value
          : rawValue(configuredValue.value);
    }
  }

  return config;
}

// ─── Valore di una condizione (blocco SE): editor <-> AutomationCondition.value ─────────
//
// `value` viaggia come stringa JSON (colonna `valueJson`) nella forma a OGGETTO della specifica
// (§9.5): la CHIAVE dipende da sorgente e arita' dell'operatore, il TIPO del contenuto dal
// `ParamType` del campo (dal catalogo condizioni).
//
//   STATIC + SINGLE   {"value": "Inviato"} · {"value": 10000, "currency": "EUR"} · {"value": true}
//   STATIC + RANGE    {"from": 100, "to": 500}
//   STATIC + SET      {"values": ["SENT", "DRAFT"]}
//   STATIC + RELATIVE {"value": 30, "unit": "DAY"}   (solo sulle date; unit DAY se assente)
//   FIELD             {"field": "lastContactDate"}
//
// L'editor lavora invece su STRINGHE grezze: scalare per SINGLE, array JSON di stringhe per
// RANGE/SET/RELATIVE, `fieldName` per FIELD. Queste due funzioni sono l'UNICO punto di traduzione fra le due
// rappresentazioni: `...ToDto` al submit, `...FromDto` all'idratazione del form in edit.

/**
 * Valuta implicita degli importi. Non e' un dato del catalogo: l'editor suffissa gia' i MONEY con
 * ' €' senza chiedere a nessuno, quindi l'assunzione esisteva — qui diventa esplicita. Unico punto
 * da toccare il giorno in cui il CRM diventera' multi-valuta.
 */
const MONEY_CURRENCY = 'EUR';

/** Deserializza il valore multi-slot (RANGE/SET/RELATIVE) dell'editor: array di stringhe; [] se vuoto/rotto. */
export function parseJsonArray(value: string): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * Stringa dell'editor -> scalare JSON tipizzato dal `ParamType`: i numerici diventano `number`, i
 * BOOLEAN `boolean`, il resto (STRING/DATE/ENUM) resta `string`. `null` se il testo non e' un
 * numero valido (input parziale: il submit non deve spedire `NaN`).
 *
 * Esportata perche' la usa anche il form di configurazione dell'azione "aggiorna campo": la sua
 * config viaggia opaca e nessun mapper la tipizza, quindi a farlo e' il form stesso — l'unico che
 * conosca il `type` del campo scelto. Stessa regola, applicata prima invece che al boundary.
 */
export function typedValue(
  type: AutomationParamType,
  raw: string,
): string | number | boolean | null {
  switch (type) {
    case 'INTEGER':
    case 'DECIMAL':
    case 'MONEY': {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    }
    case 'BOOLEAN':
      return raw === 'true';
    default:
      return raw;
  }
}

/** Inverso di `typedValue`: dal JSON alla stringa che l'editor sa rendere ('' se assente). */
const rawValue = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * Editor -> DTO: costruisce l'oggetto JSON di `AutomationCondition.value`. `null` se non c'e'
 * ancora un valore (riga incompleta): la colonna resta vuota invece di ospitare `{"value": ""}`.
 */
export function conditionValueToDto(
  type: AutomationParamType,
  cardinality: OperatorCardinality,
  valueSource: EditableValueSource,
  value: string,
): string | null {
  if (!value) return null;

  // FIELD: il termine destro e' un altro campo del record, non un letterale — nessun tipo da
  // applicare, il contenuto e' il suo `fieldName`.
  if (valueSource === 'FIELD') return JSON.stringify({ field: value });

  const currency = type === 'MONEY' ? { currency: MONEY_CURRENCY } : {};

  if (cardinality === 'RANGE') {
    const [from = '', to = ''] = parseJsonArray(value);
    return JSON.stringify({ from: typedValue(type, from), to: typedValue(type, to), ...currency });
  }

  if (cardinality === 'SET') {
    const values = parseJsonArray(value).map((v) => typedValue(type, v));
    return values.length > 0 ? JSON.stringify({ values, ...currency }) : null;
  }

  // RELATIVE: la quantità è un intero qualunque sia il tipo del campo (è una data), e senza
  // quantità la riga è incompleta anche se l'unità c'è — Number('') varrebbe 0, cioè «oggi».
  if (cardinality === 'RELATIVE') {
    const [amount = '', unit = DEFAULT_RELATIVE_UNIT] = parseJsonArray(value);
    const typedAmount = amount === '' ? null : typedValue('INTEGER', amount);

    return typedAmount === null ? null : JSON.stringify({ value: typedAmount, unit });
  }

  return JSON.stringify({ value: typedValue(type, value), ...currency });
}

/**
 * DTO -> editor: legge il valore configurato e torna la stringa grezza attesa dall'editor ('' se manca o e'
 * illeggibile — una condizione salvata male non deve rompere il form, al massimo si ri-compila).
 */
export function conditionValueFromDto(
  cardinality: OperatorCardinality,
  valueSource: EditableValueSource,
  value: string | null,
): string {
  if (!value) return '';

  let configuredValue: Record<string, unknown>;

  try {
    const parsed: unknown = JSON.parse(value);

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return '';

    configuredValue = parsed as Record<string, unknown>;
  } catch {
    return '';
  }

  if (valueSource === 'FIELD') return rawValue(configuredValue.field);

  if (cardinality === 'RANGE') {
    const from = rawValue(configuredValue.from);
    const to = rawValue(configuredValue.to);

    return from || to ? JSON.stringify([from, to]) : '';
  }

  if (cardinality === 'SET') {
    const values = Array.isArray(configuredValue.values)
      ? configuredValue.values.map(rawValue)
      : [];

    return values.length > 0 ? JSON.stringify(values) : '';
  }

  if (cardinality === 'RELATIVE') {
    const amount = rawValue(configuredValue.value);
    const unit = rawValue(configuredValue.unit) || DEFAULT_RELATIVE_UNIT;

    return amount ? JSON.stringify([amount, unit]) : '';
  }

  return rawValue(configuredValue.value);
}

// ─── Configurazione dell'azione (blocco ALLORA): editor <-> AutomationAction.config ──
//
// A differenza di `eventConfig` e del valore di una condizione, qui il mapper NON guarda dentro.
// La config e' OPACA: la sua forma la conoscono i due capi — il form che la compone e il provider
// Java che la valida — e tutto cio' che sta in mezzo la trasporta e basta. E' questo che permette
// di aggiungere un'azione senza toccare serializzazione, persistenza e contratto REST.
//
// Ne segue che qui non si tipizza nulla: i valori arrivano gia' tipizzati dal form, che e' l'unico
// a conoscere il `type` del campo scelto (cfr. `typedValue`).

/**
 * Config dell'editor -> `AutomationAction.config` (stringa JSON grezza, come la vuole il contratto
 * REST e come la persiste la colonna).
 *
 * Config assente o VUOTA danno entrambe `null`: un oggetto vuoto significherebbe "configurata, ma
 * senza contenuto", e il provider la giudicherebbe incompleta invece che mancante. Sono due
 * messaggi diversi per l'utente, e la differenza va conservata fin da qui.
 */
export function actionConfigToDto(config: Record<string, unknown> | null): string | null {
  if (!config || Object.keys(config).length === 0) return null;

  return JSON.stringify(config);
}

/**
 * `AutomationAction.config` -> config dell'editor. Inverso di {@link actionConfigToDto}.
 *
 * `null` se assente, illeggibile o non un oggetto: una config salvata male non deve rompere
 * l'idratazione del builder, e per il form e' indistinguibile da un'azione mai configurata — che e'
 * esattamente cio' che e', dal suo punto di vista.
 */
export function actionConfigFromDto(config: string | null): Record<string, unknown> | null {
  if (!config) return null;

  try {
    const parsed: unknown = JSON.parse(config);

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}
